import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Smartphone, Monitor, LogOut, Lock, Eye, EyeOff } from 'lucide-react';

export default function SecuritySection({ sessions, passwordForm, setPasswordForm, showPasswords, setShowPasswords, changingPassword, handleChangePassword, revokeSession, revokeAllOtherSessions }) {
  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="font-serif text-xl font-semibold text-[#1a3a5c] mb-2">Change Password</h3>
        <p className="text-sm text-gray-600 mb-6">Update your password to keep your account secure</p>
        <form onSubmit={handleChangePassword} className="space-y-4 max-w-md" data-testid="change-password-form">
          {[
            { key: 'current', label: 'Current Password', placeholder: 'Enter current password' },
            { key: 'new_password', label: 'New Password', placeholder: 'Enter new password (min 8 chars)' },
            { key: 'confirm', label: 'Confirm New Password', placeholder: 'Confirm new password' }
          ].map(({ key, label, placeholder }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
              <div className="relative">
                <input type={showPasswords[key] ? 'text' : 'password'} value={passwordForm[key]} onChange={(e) => setPasswordForm({ ...passwordForm, [key]: e.target.value })} placeholder={placeholder} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A967] focus:border-transparent pr-12" required data-testid={`password-${key}`} />
                <button type="button" onClick={() => setShowPasswords({ ...showPasswords, [key]: !showPasswords[key] })} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPasswords[key] ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
          ))}
          <button type="submit" disabled={changingPassword} className="px-6 py-2.5 bg-[#1a3a5c] text-white rounded-lg hover:bg-[#1f4268] transition-colors disabled:opacity-50 flex items-center gap-2" data-testid="change-password-btn">
            <Lock className="h-4 w-4" />{changingPassword ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>

      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-serif text-xl font-semibold text-[#1a3a5c]">Active Sessions</h3>
            <p className="text-sm text-gray-600">Manage devices where you are logged in</p>
          </div>
          {sessions.filter(s => !s.is_current).length > 0 && (
            <button onClick={revokeAllOtherSessions} className="flex items-center space-x-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors" data-testid="logout-all-devices">
              <LogOut className="h-4 w-4" /><span className="text-sm font-medium">Logout from all other devices</span>
            </button>
          )}
        </div>
        {sessions.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No active sessions found</p>
        ) : (
          <div className="space-y-4">
            {sessions.map((session) => (
              <motion.div key={session.session_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className={`flex items-center justify-between p-4 rounded-lg ${session.is_current ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`} data-testid={`session-${session.session_id}`}>
                <div className="flex items-center space-x-4">
                  <div className={`p-3 rounded-full ${session.is_current ? 'bg-green-100' : 'bg-gray-200'}`}>
                    {session.device_info?.includes('Mobile') ? <Smartphone className={`h-6 w-6 ${session.is_current ? 'text-green-600' : 'text-gray-600'}`} /> : <Monitor className={`h-6 w-6 ${session.is_current ? 'text-green-600' : 'text-gray-600'}`} />}
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <p className="font-medium text-gray-900">{session.device_info || 'Unknown Device'}</p>
                      {session.is_current && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">This device</span>}
                      {session.remember_me && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">Remember me</span>}
                    </div>
                    <p className="text-sm text-gray-500">{session.ip_address || 'Unknown IP'} • Last active: {new Date(session.last_active).toLocaleString()}</p>
                    <p className="text-xs text-gray-400">Created: {new Date(session.created_at).toLocaleString()} • Expires: {new Date(session.expires_at).toLocaleDateString()}</p>
                  </div>
                </div>
                {!session.is_current && (
                  <button onClick={() => revokeSession(session.session_id)} className="flex items-center space-x-1 px-3 py-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors" data-testid={`revoke-session-${session.session_id}`}>
                    <LogOut className="h-4 w-4" /><span className="text-sm font-medium">Logout</span>
                  </button>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
        <p className="text-sm text-amber-800"><strong>Security tip:</strong> If you see any unfamiliar sessions, click Logout to revoke access. Consider changing your password if you suspect unauthorized access.</p>
      </div>
    </div>
  );
}
